/*
 * <ds-drawer> — the one right-side overlay primitive.
 *
 *   <ds-drawer id="X" data-size="md|lg|full" hidden>
 *     <div class="ds-drawer__scrim" data-drawer-close></div>
 *     <section class="ds-drawer__panel" role="dialog" aria-modal="true" aria-labelledby="X-title" tabindex="-1">
 *       <header class="ds-drawer__head">
 *         <h2 id="X-title" class="ds-drawer__title">…</h2>
 *         <button type="button" class="ds-drawer__close ds-icon-btn" data-drawer-close aria-label="Close"></button>
 *       </header>
 *       <div class="ds-drawer__body">…</div>
 *       <footer class="ds-drawer__foot">…</footer>
 *     </section>
 *   </ds-drawer>
 *   <button data-drawer-open="X" aria-haspopup="dialog" aria-expanded="false">…</button>
 *
 * Behaviour is extended from outside through hooks assigned on the element
 * (drawer.onBeforeOpen / onOpen / onBeforeClose / onClose) or by listening for
 * the `ds-drawer:open` / `ds-drawer:close` events.
 */
(() => {
  if (customElements.get('ds-drawer')) return;

  const CLOSE_ICON =
    '<svg class="ds-icon" viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false"><path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
  const SKELETON =
    '<div class="ds-drawer__skeleton" aria-hidden="true"><span></span><span></span><span></span><span></span></div>';

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const focusableSelector =
    'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex^="-"])';

  class DsDrawer extends HTMLElement {
    static stack = [];

    static get top() {
      return DsDrawer.stack[DsDrawer.stack.length - 1] || null;
    }

    static get(id) {
      const el = document.getElementById(id);
      return el instanceof DsDrawer ? el : null;
    }

    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this.panel = this.querySelector(':scope > .ds-drawer__panel');
      this.body = this.panel && this.panel.querySelector('.ds-drawer__body');
      this.classList.add('is-closed');
      if (!this.hasAttribute('hidden')) this.setAttribute('hidden', '');

      this.querySelectorAll('.ds-drawer__close').forEach((button) => {
        if (!button.innerHTML.trim()) button.innerHTML = CLOSE_ICON;
      });

      this.addEventListener('click', (event) => {
        const closer = event.target.closest('[data-drawer-close]');
        if (!closer || closer.closest('ds-drawer') !== this) return;
        event.preventDefault();
        this.close({ reason: closer.classList.contains('ds-drawer__scrim') ? 'scrim' : 'button' });
      });

      this._onTrapKeydown = this.onTrapKeydown.bind(this);
      this._onTransitionEnd = this.onTransitionEnd.bind(this);
      if (this.panel) this.panel.addEventListener('transitionend', this._onTransitionEnd);
    }

    /* Stack membership, not the is-open class: the class lands a frame later. */
    get isOpen() {
      return DsDrawer.stack.includes(this);
    }

    toggle(options = {}) {
      return this.isOpen ? this.close(options) : this.open(options);
    }

    open({ trigger = null } = {}) {
      if (this.isOpen) return true;
      if (typeof this.onBeforeOpen === 'function' && this.onBeforeOpen({ trigger }) === false) return false;

      // One drawer at a time: opening this one closes whatever else is open.
      DsDrawer.stack.slice().forEach((drawer) => {
        if (drawer !== this) drawer.close({ reason: 'replaced', restoreFocus: false });
      });

      this._trigger = trigger || (document.activeElement !== document.body ? document.activeElement : null);
      this.setTriggerExpanded(true);

      this.removeAttribute('hidden');
      this.classList.remove('is-closed');
      void this.offsetWidth;

      DsDrawer.stack.push(this);
      if (window.lockBodyScroll) window.lockBodyScroll(this.lockOwner);
      this.setPageInert(true);
      this.maybeLoadSource();

      requestAnimationFrame(() => {
        if (!this.isOpen) return;
        this.classList.add('is-open');
        if (reducedMotion() || !this.hasTransition()) {
          requestAnimationFrame(() => requestAnimationFrame(() => this.armFocus()));
        } else {
          this._armOnTransition = true;
        }
      });

      this.dispatchEvent(new CustomEvent('ds-drawer:open', { bubbles: true, detail: { trigger: this._trigger } }));
      if (typeof this.onOpen === 'function') this.onOpen({ trigger: this._trigger });
      return true;
    }

    close({ reason = 'api', restoreFocus = true } = {}) {
      if (!this.isOpen && !DsDrawer.stack.includes(this)) return true;
      if (typeof this.onBeforeClose === 'function' && this.onBeforeClose({ reason }) === false) return false;

      this.classList.remove('is-open');
      this._armOnTransition = false;
      DsDrawer.stack = DsDrawer.stack.filter((drawer) => drawer !== this);
      this.releaseFocus();
      if (window.unlockBodyScroll) window.unlockBodyScroll(this.lockOwner);
      if (!DsDrawer.stack.length) this.setPageInert(false);
      this.setTriggerExpanded(false);

      if (this._loadController) this._loadController.abort();

      if (reducedMotion() || !this.hasTransition()) this.collapse();

      const trigger = this._trigger;
      this._trigger = null;
      if (restoreFocus && trigger && document.contains(trigger) && !DsDrawer.stack.length) {
        try {
          trigger.focus({ preventScroll: true });
        } catch (error) {
          trigger.focus();
        }
      }

      this.dispatchEvent(new CustomEvent('ds-drawer:close', { bubbles: true, detail: { reason } }));
      if (typeof this.onClose === 'function') this.onClose({ reason });
      return true;
    }

    get lockOwner() {
      return `ds-drawer:${this.id || 'anon'}`;
    }

    hasTransition() {
      if (!this.panel) return false;
      const style = getComputedStyle(this.panel);
      return style.transitionDuration.split(',').some((value) => parseFloat(value) > 0);
    }

    onTransitionEnd(event) {
      if (event.target !== this.panel) return;
      if (this.isOpen) {
        if (this._armOnTransition) {
          this._armOnTransition = false;
          this.armFocus();
        }
      } else {
        this.collapse();
      }
    }

    collapse() {
      if (this.isOpen) return;
      this.classList.add('is-closed');
      this.setAttribute('hidden', '');
    }

    armFocus() {
      if (!this.isOpen || !this.panel) return;
      const preferred =
        this.panel.querySelector('[data-drawer-autofocus]') ||
        this.panel.querySelector('.ds-drawer__close') ||
        this.panel;
      document.addEventListener('keydown', this._onTrapKeydown, true);
      try {
        preferred.focus({ preventScroll: true });
      } catch (error) {
        preferred.focus();
      }
    }

    releaseFocus() {
      document.removeEventListener('keydown', this._onTrapKeydown, true);
    }

    onTrapKeydown(event) {
      if (event.key !== 'Tab' || DsDrawer.top !== this || !this.panel) return;
      const focusables = Array.from(this.panel.querySelectorAll(focusableSelector)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );
      if (!focusables.length) {
        event.preventDefault();
        this.panel.focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (!this.panel.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && (active === first || active === this.panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    setTriggerExpanded(expanded) {
      const triggers = new Set();
      if (this._trigger && this._trigger.hasAttribute && this._trigger.hasAttribute('aria-expanded')) {
        triggers.add(this._trigger);
      }
      if (this.id) {
        document.querySelectorAll(`[data-drawer-open="${CSS.escape(this.id)}"]`).forEach((el) => triggers.add(el));
      }
      triggers.forEach((el) => el.setAttribute('aria-expanded', String(expanded)));
    }

    /* Page content behind the drawer becomes inert: the main column, the
       header group and the footer group. When one of those contains this
       drawer, its siblings along the ancestor chain are inerted instead. */
    setPageInert(inert) {
      if (!inert) {
        document.querySelectorAll('[data-ds-inerted]').forEach((el) => {
          el.removeAttribute('inert');
          delete el.dataset.dsInerted;
        });
        return;
      }
      const mark = (el) => {
        if (el.hasAttribute('inert') || el.matches('script, style, link, template')) return;
        el.setAttribute('inert', '');
        el.dataset.dsInerted = 'true';
      };
      const targets = document.querySelectorAll(
        '#MainContent, .shopify-section-group-header-group, .shopify-section-group-footer-group, [data-ds-inert-target]'
      );
      targets.forEach((el) => {
        if (!el.contains(this)) {
          mark(el);
          return;
        }
        for (let node = this; node && node !== el; node = node.parentElement) {
          Array.from(node.parentElement?.children || []).forEach((sibling) => {
            if (sibling !== node && !(sibling instanceof DsDrawer)) mark(sibling);
          });
        }
      });
    }

    /* data-drawer-src="/path?section_id=foo" — fetched once on first open. */
    maybeLoadSource() {
      const src = this.dataset.drawerSrc;
      if (!src || this._sourceLoaded || !this.body) return;
      this.load(src).then((ok) => {
        if (ok) this._sourceLoaded = true;
      });
    }

    /**
     * Fetch a section-rendered URL into the body. Aborted when the drawer
     * closes. Resolves true on success, false on abort/failure.
     */
    async load(url, { target = this.body, select = null } = {}) {
      if (!target) return false;
      if (this._loadController) this._loadController.abort();
      const controller = new AbortController();
      this._loadController = controller;
      target.setAttribute('aria-busy', 'true');
      target.innerHTML = SKELETON;
      try {
        const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const source =
          (select && doc.querySelector(select)) ||
          doc.querySelector('[data-drawer-content]') ||
          doc.querySelector('.shopify-section') ||
          doc.body;
        target.innerHTML = '';
        Array.from(source.childNodes).forEach((node) => target.appendChild(document.importNode(node, true)));
        target.querySelectorAll('script').forEach((old) => {
          const script = document.createElement('script');
          Array.from(old.attributes).forEach((attr) => script.setAttribute(attr.name, attr.value));
          script.textContent = old.textContent;
          old.replaceWith(script);
        });
        target.removeAttribute('aria-busy');
        this.dispatchEvent(new CustomEvent('ds-drawer:loaded', { bubbles: true, detail: { url } }));
        return true;
      } catch (error) {
        target.removeAttribute('aria-busy');
        if (error.name === 'AbortError') return false;
        target.innerHTML =
          '<p class="ds-drawer__error" role="alert">This couldn’t load. Check your connection and try again.</p>';
        return false;
      } finally {
        if (this._loadController === controller) this._loadController = null;
      }
    }
  }

  customElements.define('ds-drawer', DsDrawer);
  window.DsDrawer = DsDrawer;

  // Escape closes only the top-most drawer.
  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Escape' || !DsDrawer.top) return;
      if (event.defaultPrevented) return;
      event.stopPropagation();
      event.preventDefault();
      DsDrawer.top.close({ reason: 'escape' });
    },
    true
  );

  // Delegated triggers anywhere in the document.
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-drawer-open]');
    if (!trigger) return;
    const drawer = DsDrawer.get(trigger.dataset.drawerOpen);
    if (!drawer) return;
    event.preventDefault();
    if (drawer.isOpen) {
      drawer.close({ reason: 'trigger' });
    } else {
      drawer.open({ trigger });
    }
  });
})();
